import type { Loader } from "jd-core-typescript-port";

import type { ZipEntry } from "../archive/ZipArchive.js";
import { ZipArchive } from "../archive/ZipArchive.js";

export interface ArchiveClassEntry {
  readonly path: string;
  readonly internalName: string;
  readonly version: number;
  readonly zipEntry: ZipEntry;
}

const CLASS_ROOT_PREFIXES = ["classes/", "BOOT-INF/classes/", "WEB-INF/classes/"];

function normalizePath(path: string): string {
  let normalized = path.replaceAll("\\", "/");
  while (normalized.startsWith("./")) {
    normalized = normalized.slice(2);
  }
  while (normalized.startsWith("/")) {
    normalized = normalized.slice(1);
  }
  return normalized;
}

export function classEntryFromPath(
  path: string,
  zipEntry: ZipEntry,
): ArchiveClassEntry | null {
  const normalizedPath = normalizePath(path);
  if (!normalizedPath.endsWith(".class")) {
    return null;
  }

  let classPath = normalizedPath;
  let version = 0;

  const multiReleaseMatch = /^META-INF\/versions\/(\d+)\/(.+)$/u.exec(classPath);
  if (multiReleaseMatch !== null) {
    version = Number.parseInt(multiReleaseMatch[1]!, 10);
    classPath = multiReleaseMatch[2]!;
  }

  for (const prefix of CLASS_ROOT_PREFIXES) {
    if (classPath.startsWith(prefix)) {
      classPath = classPath.slice(prefix.length);
      break;
    }
  }

  const internalName = classPath.slice(0, -".class".length);
  if (internalName.length === 0) {
    return null;
  }

  return {
    path: normalizedPath,
    internalName,
    version,
    zipEntry,
  };
}

function preferredEntry(entries: readonly ArchiveClassEntry[]): ArchiveClassEntry {
  let preferred = entries[0]!;

  for (let index = 1; index < entries.length; index++) {
    const candidate = entries[index]!;
    if (candidate.version >= preferred.version) {
      preferred = candidate;
    }
  }

  return preferred;
}

/** Loader backed by a lazily inflated ZIP, JAR, or JMOD archive. */
export class ArchiveLoader implements Loader {
  private readonly classesByInternalName = new Map<
    string,
    ArchiveClassEntry[]
  >();
  private readonly classEntries: ArchiveClassEntry[] = [];

  public constructor(public readonly archive: ZipArchive) {
    for (const entry of archive.entries) {
      if (entry.directory) {
        continue;
      }

      const classEntry = classEntryFromPath(entry.name, entry);
      if (classEntry === null) {
        continue;
      }

      this.classEntries.push(classEntry);
      const candidates = this.classesByInternalName.get(classEntry.internalName);
      if (candidates === undefined) {
        this.classesByInternalName.set(classEntry.internalName, [classEntry]);
      } else {
        candidates.push(classEntry);
      }
    }
  }

  public getClasses(): readonly ArchiveClassEntry[] {
    return this.classEntries;
  }

  public canLoad(internalName: string): boolean {
    return this.classesByInternalName.has(internalName);
  }

  public load(internalName: string): Uint8Array | null {
    const entries = this.classesByInternalName.get(internalName);
    if (entries === undefined || entries.length === 0) {
      return null;
    }

    return this.loadEntry(preferredEntry(entries));
  }

  public loadEntry(entry: ArchiveClassEntry): Uint8Array {
    return this.archive.extract(entry.zipEntry.name);
  }

  public scopedTo(entry: ArchiveClassEntry): Loader {
    return new ScopedArchiveLoader(this, entry);
  }
}

class ScopedArchiveLoader implements Loader {
  private readonly archiveLoader: ArchiveLoader;
  private readonly selectedEntry: ArchiveClassEntry;

  public constructor(
    archiveLoader: ArchiveLoader,
    selectedEntry: ArchiveClassEntry,
  ) {
    this.archiveLoader = archiveLoader;
    this.selectedEntry = selectedEntry;
  }

  public canLoad(internalName: string): boolean {
    return (
      internalName === this.selectedEntry.internalName ||
      this.archiveLoader.canLoad(internalName)
    );
  }

  public load(internalName: string): Uint8Array | null {
    if (internalName === this.selectedEntry.internalName) {
      return this.archiveLoader.loadEntry(this.selectedEntry);
    }

    return this.archiveLoader.load(internalName);
  }
}

export class SingleClassLoader implements Loader {
  private readonly internalName: string;
  private readonly bytes: Uint8Array;

  public constructor(internalName: string, bytes: Uint8Array) {
    this.internalName = internalName;
    this.bytes = bytes;
  }

  public canLoad(internalName: string): boolean {
    return internalName === this.internalName;
  }

  public load(internalName: string): Uint8Array | null {
    return internalName === this.internalName ? this.bytes : null;
  }
}
